import type { EligibilityCheckRequest, EligibilityViewStatus } from '@galapagos/shared';
import type { SignupApi } from '../services/signup-api/signup-api';
import { useSignupFlowStore } from '../stores/signup-flow-store';

export const ELIGIBILITY_POLL_INTERVAL_MS = 3000;
export const ELIGIBILITY_POLL_MAX_READS = 20;

type EligibilityApi = Pick<SignupApi, 'submitEligibility' | 'getEligibilityStatus'>;

// Module state, not component state: the check keeps running after the member
// leaves the step that started it. `generation` makes callbacks from a reset
// or superseded run harmless.
let started = false;
let firstClientRef: string | undefined;
let followUpSent = false;
let generation = 0;
let timer: ReturnType<typeof setTimeout> | null = null;

function isResolved(status: EligibilityViewStatus): boolean {
  return status === 'eligible' || status === 'needs_review';
}

function record(status: EligibilityViewStatus): void {
  useSignupFlowStore.getState().setEligibilityStatus(status);
}

// An absent or empty key list from the server means no restriction, kept as [].
function recordEligiblePrograms(programKeys: string[] | undefined): void {
  useSignupFlowStore.getState().setEligibleProgramKeys(programKeys ?? []);
}

function scheduleRead(api: EligibilityApi, read: number, run: number): void {
  if (read > ELIGIBILITY_POLL_MAX_READS) return;
  timer = setTimeout(() => {
    timer = null;
    const next = () => {
      if (run === generation) scheduleRead(api, read + 1, run);
    };
    api.getEligibilityStatus().then(
      (view) => {
        if (run !== generation) return;
        if (view.status === 'eligible') recordEligiblePrograms(view.programKeys);
        record(view.status);
        if (!isResolved(view.status)) next();
      },
      // A failed read is skipped quietly; it still counts toward the cap.
      next,
    );
  }, ELIGIBILITY_POLL_INTERVAL_MS);
}

function stopPolling(): void {
  generation += 1;
  if (timer) clearTimeout(timer);
  timer = null;
}

/**
 * A link with no employer checks first from the name and birth date alone;
 * once the member picks an employer, one more check names it. Any other
 * repeat is refused.
 */
function mayFollowUp(request: EligibilityCheckRequest): boolean {
  const clientRef = clientRefOf(request);
  return !followUpSent && clientRef !== undefined && clientRef !== firstClientRef;
}

/** The sponsor a check names; a SAML-session check names none. */
function clientRefOf(request: EligibilityCheckRequest): string | undefined {
  return 'clientRef' in request ? request.clientRef : undefined;
}

/**
 * Submits the eligibility check once per page session and follows it quietly:
 * the member is never blocked and never shown the result, matching
 * production. Each status lands in the flow store for later steps. One
 * follow-up is allowed when it names a sponsor the first check did not; it
 * replaces the first check's polling. Returns false when nothing was sent.
 * `onSubmitFailed` lets a caller that waits on the answer stop waiting.
 */
export function startQuietEligibilityCheck(
  api: EligibilityApi,
  request: EligibilityCheckRequest,
  onSubmitFailed?: (error: unknown) => void,
): boolean {
  if (started) {
    if (!mayFollowUp(request)) return false;
    followUpSent = true;
    stopPolling();
  } else {
    started = true;
    firstClientRef = clientRefOf(request);
  }
  const run = generation;
  api.submitEligibility(request).then(
    (response) => {
      if (run !== generation) return;
      record(response.status);
      if (response.status === 'pending') scheduleRead(api, 1, run);
    },
    // Production swallows a failed check too; registration goes on.
    (error: unknown) => {
      if (run === generation) onSubmitFailed?.(error);
    },
  );
  return true;
}

/** Forgets any started check and stops its polling (a fresh flow, or a test). */
export function resetQuietEligibilityCheck(): void {
  started = false;
  firstClientRef = undefined;
  followUpSent = false;
  stopPolling();
}

// Whenever the store clears the answers (a fresh flow, a new sponsor, a session
// end), the check that belonged to them is dropped too, so the next flow can
// submit its own and a late answer never lands in it.
useSignupFlowStore.subscribe((state, previous) => {
  if (state.flowEpoch !== previous.flowEpoch) resetQuietEligibilityCheck();
});
